<template>
  <div>
    <video :id :aria-label class="cld-video-player" />
  </div>
</template>

<script lang="ts" setup>
import type { VideoTrack } from '#types/media'

const {
  autoplay = true,
  ariaLabel,
  cloudinaryAsset,
  duration,
  height,
  hideControls = true,
  loop = true,
  muted = true,
  name,
  onVideoSubtitle,
  onVideoTitle,
  posterUrl,
  seoTitle,
  seoDescription,
  uploadDate,
  url,
  width,
  tracks = [],
} = defineProps<{
  autoplay?: boolean
  ariaLabel?: string
  cloudinaryAsset?: {
    cloudName: string
    publicId: string
  }
  duration?: number
  height?: number
  hideControls?: boolean
  loop?: boolean
  muted?: boolean
  name?: string
  onVideoTitle?: string
  onVideoSubtitle?: string
  posterUrl?: string
  seoTitle?: string
  seoDescription?: string
  url?: string
  width?: number
  uploadDate?: string
  tracks?: VideoTrack[]
}>()

const locale = useLocale()
const id = useId()

const findDefaultTrack = (tracks) => {
  const bestMatch = tracks.find(({ language }) => language === locale)

  if (bestMatch) {
    return bestMatch
  }

  return tracks.find(({ language }) => locale.includes(language)) || tracks[0]
}

onMounted(async () => {
  import('cloudinary-video-player/cld-video-player.min.css')
  const cloudinary = await import('cloudinary-video-player')
  // @ts-expect-error: No types for cloudinary-video-player/chapters
  await import('cloudinary-video-player/chapters')

  const tracksMap = tracks.map(({ src, label, srclang, kind }) => ({
    kind,
    url: src,
    label,
    language: srclang,
  }))

  const transcripts = tracksMap.filter((t) => t.kind === 'transcripts')
  const defaultTrack = findDefaultTrack(transcripts)
  const captions = transcripts.map((t) => ({
    ...t,
    default: t === defaultTrack,
  }))
  const chapters = tracksMap.find((t) => t.kind === 'chapters')
  const player = cloudinary.videoPlayer(id, {
    autoplay,
    ...(autoplay && { autoplayMode: 'on-scroll' }),
    chaptersButton: true,
    cloudName: cloudinaryAsset!.cloudName,
    controls: !hideControls,
    fluid: true,
    height,
    hideContextMenu: true,
    loop,
    muted,
    showLogo: false,
    width,
  })

  if (ariaLabel) {
    player.el().setAttribute('aria-label', ariaLabel)
  }

  player.source(cloudinaryAsset!.publicId, {
    info: {
      title: onVideoTitle,
      subtitle: onVideoSubtitle,
    },
    ...(posterUrl && { poster: posterUrl }),
    textTracks: {
      captions,
    },
    chapters
  })
})

useHead(() => ({
  style: [
    {
      textContent: `
           .vjs-menu {
             right: 0 !important;
             left: auto !important;
           }

           .vjs-menu-content {
             right: 0 !important;
             left: auto !important;
           }

           .vjs-control-bar .vjs-control:focus-visible, .vjs-volume-control:focus-within {
              outline: -webkit-focus-ring-color auto !important;
            }
          `
    }
  ],
  script: [
    {
      key: `cloudinary-video-schema-${id}`,
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        ...(name ? { name } : {}),
        ...(seoTitle ? { headline: seoTitle } : {}),
        ...(seoDescription ? { description: seoDescription } : {}),
        ...(posterUrl ? { thumbnailUrl: posterUrl } : {}),
        ...(url ? { contentUrl: url } : {}),
        ...(uploadDate ? { uploadDate } : {}),
        'inLanguage': locale,
        ...(duration ? { duration: `PT${Math.round(duration)}S` } : {}),
      })
    }
  ]
}))
</script>
